import React, { useMemo, useEffect } from "react";
import {
  Button,
  Checkbox,
  Label,
  Text,
  Flex,
  ErrorMessage,
  Banner,
  HelpIndicator,
} from "@jsq/ds/core";
import { useFormContext, SubmitButton, FormTextarea } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { ExtendedInfoTooltip } from "~/pages/Compliance/Common/ExtendedInfoTooltip";
import { type ContactEligibility } from "../index";
import {
  selectAllNoneText,
  greyText,
  instructionText,
  portalContactsLabel,
  selectText,
  separatorText,
  buttonSection,
  cancelButtonMargin,
  customMessageContainer,
} from "../styles_vanilla.css";
import { FormValues } from "../types";
import { getRemediationBanner } from "../utils";

interface Props {
  contactsEligibility: ContactEligibility[];
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  loading?: boolean;
  contactsError?: Error;
  submitError?: string | null;
  dateSent?: string;
  sentCount?: number;
}

const InviteInvestorFormContent = ({
  contactsEligibility,
  onClose,
  loading,
  contactsError,
  submitError,
  dateSent,
  sentCount,
}: Props) => {
  const { values, setFieldValue, errors, touched, setFieldTouched } =
    useFormContext<FormValues>();

  const remediationBanner = useMemo(
    () => getRemediationBanner(dateSent, sentCount),
    [dateSent, sentCount],
  );

  const eligibleEmails = useMemo(
    () =>
      contactsEligibility
        .filter((contact) => contact.isEligible)
        .map((contact) => contact.email),
    [contactsEligibility],
  );

  const handleSelectEmail = (email: string, isEligible: boolean) => {
    if (!isEligible) return;

    const currentSelected = values.selectedEmails || [];
    const newSelected = currentSelected.includes(email)
      ? currentSelected.filter((e) => e !== email)
      : [...currentSelected, email];

    void setFieldValue("selectedEmails", newSelected);
  };

  const handleSelectAllEmails = () => {
    void setFieldValue("selectedEmails", eligibleEmails);
  };

  const handleSelectNone = () => {
    void setFieldValue("selectedEmails", []);
  };

  const handleToggleCustomMessage = () => {
    void setFieldValue("showCustomMessage", !values.showCustomMessage);
  };

  const selectedEmails = values.selectedEmails || [];

  const messageLength = values.message?.length || 0;
  const remainingCharacters = Math.max(0, 400 - messageLength);
  const isOverLimit = messageLength > 400;

  useEffect(() => {
    if (isOverLimit && values.showCustomMessage) {
      void setFieldTouched("message", true);
    }
  }, [isOverLimit, values.showCustomMessage, setFieldTouched]);

  // Show error only if field has been touched and there's an error
  const showEmailError = touched.selectedEmails && errors.selectedEmails;
  const emailErrorMessage = errors.selectedEmails?.toString() || "";

  return (
    <>
      {remediationBanner && (
        <Banner
          variant="primary"
          title="Remediation request sent already"
          description={
            <Flex flexDirection="column" gap={Sizes.XS}>
              <Text variant="regular-md">{remediationBanner.mainText}</Text>
              {remediationBanner.requestsSent && (
                <>
                  <Text variant="regular-md">
                    Requests sent: {remediationBanner.requestsSent}
                  </Text>
                  <Text variant="regular-md">
                    Last requested: {remediationBanner.lastRequested}
                  </Text>
                </>
              )}
            </Flex>
          }
        />
      )}
      <Text variant="regular-md" className={instructionText}>
        An email will be sent prompting the investor to set up a compliance profile for this
        position.
      </Text>
      <Label required={true} className={portalContactsLabel}>
        Portal Contacts
      </Label>
      <Flex alignItems="center" gap="xs" className={selectAllNoneText}>
        <Text variant="strong-md" className={selectText}>
          Select
        </Text>
        <Button displayAsLink onClick={handleSelectAllEmails}>
          All
        </Button>
        <Text variant="strong-md" className={separatorText}>
          |
        </Text>
        <Button displayAsLink onClick={handleSelectNone}>
          None
        </Button>
      </Flex>
      {contactsEligibility.length > 0 &&
        contactsEligibility.map((contact) => {
          if (!contact.isEligible && contact.disabledReason) {
            return (
              <Flex key={contact.email} className={greyText}>
                <ExtendedInfoTooltip message={contact.disabledReason}>
                  <Checkbox
                    name={contact.email}
                    label={contact.email}
                    checked={selectedEmails.includes(contact.email)}
                    onChange={() => handleSelectEmail(contact.email, contact.isEligible)}
                    disabled={!contact.isEligible}
                  />
                </ExtendedInfoTooltip>
              </Flex>
            );
          }

          return (
            <Flex key={contact.email} className={greyText}>
              <Checkbox
                name={contact.email}
                label={contact.email}
                checked={selectedEmails.includes(contact.email)}
                onChange={() => handleSelectEmail(contact.email, contact.isEligible)}
                disabled={!contact.isEligible}
              />
            </Flex>
          );
        })}
      {showEmailError && emailErrorMessage && <ErrorMessage>{emailErrorMessage}</ErrorMessage>}
      {contactsError && (
        <ErrorMessage>Failed to fetch contacts. Please try again later.</ErrorMessage>
      )}
      <Flex flexDirection="column" gap={20} className={customMessageContainer}>
        <Button displayAsLink onClick={handleToggleCustomMessage}>
          {values.showCustomMessage
            ? "Hide customizable email message"
            : "Show customizable email message"}
        </Button>
        {values.showCustomMessage && (
          <Flex flexDirection="column">
            <Flex alignItems="center" gap={Sizes.XS}>
              <Label htmlFor="message">Email message</Label>
              <Flex alignItems="center">
                <HelpIndicator
                  id="customize-email-message-tooltip"
                  placement="top"
                  content="Customize the introductory message in the remediation request email sent to the selected contacts."
                >
                  <Icon type="question-circle" color={Colors.Neutral600} variant="outline" />
                </HelpIndicator>
              </Flex>
            </Flex>
            <FormTextarea
              id="message"
              name="message"
              isFullWidth
              size="small"
              data-testid="message"
            />
            {!isOverLimit && (
              <Text variant="regular-sm" color={Colors.Neutral500}>
                {remainingCharacters} characters remaining
              </Text>
            )}
          </Flex>
        )}
      </Flex>
      {submitError && <ErrorMessage>{submitError}</ErrorMessage>}
      <Flex justifyContent="flex-end" gap="lg" className={buttonSection}>
        <Button variant="neutral" onClick={() => onClose(false)} className={cancelButtonMargin}>
          Cancel
        </Button>
        <SubmitButton isLoading={loading}>Send request</SubmitButton>
      </Flex>
    </>
  );
};

export default InviteInvestorFormContent;
